import { AuthContext } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { useEffect, useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import esTexts from '../../translations/es.json';
import enTexts from '../../translations/en.json';
import axios from 'axios';
import Welcome from '../../components/Header/Welcome';
import { AdminNav } from '../../components';

const Universities = () => {
  const [universities, setUniversities] = useState([]);

  const { token, currentUser } = useContext(AuthContext);
  const { lang } = useLang();
  const texts = lang === 'es' ? esTexts : enTexts;

  const [newNameUniversity, setNewNameUniversity] = useState('');
  const [newSlugUniversity, setNewSlugUniversity] = useState('');
  const [idToUpdate, setIdToUpdate] = useState('');

  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getAllUniversities = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/universities`
      );
      setUniversities(response.data.universities);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveUniversity = async () => {
    if (newNameUniversity.trim() === '') {
      return alert(
        lang === 'es' ? 'Debe ingresar un nombre de universidad' : 'Please enter university name'
      );
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/universities`,
        { name: newNameUniversity, slug: newSlugUniversity },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNewNameUniversity('');
      setNewSlugUniversity('');
      setIdToUpdate('');
      getAllUniversities();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteUniversity = async id => {
    const confirm = window.confirm(texts.admin_common.delete_confirm);
    if (!confirm) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/universities/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      getAllUniversities();
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditUniversity = async id => {
    setIsUpdate(true);
    setIdToUpdate(id);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/universities/${id}`
      );
      setNewNameUniversity(response.data.university.name);
      setNewSlugUniversity(response.data.university.slug);
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateUniversity = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/universities/${idToUpdate}`,
        { name: newNameUniversity, slug: newSlugUniversity },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNewNameUniversity('');
      setNewSlugUniversity('');
      setIdToUpdate('');
      setIsUpdate(false);
      getAllUniversities();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getAllUniversities();
  }, []);

  if (!currentUser?.isAdmin) {
    return <Navigate to="/admin" />;
  }

  return (
    <>
      <Welcome />

      <div className="container py-4" style={{ minHeight: '80vh' }}>
        <AdminNav />

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-2 border-bottom">
          <div>
            <h2 className="fw-bold text-sen mb-1 d-flex align-items-center gap-2">
              <i className="fa fa-university"></i>
              <span>
                {texts.admin_common.list_of} {texts.admin.menu_universities}
              </span>
            </h2>
            <span className="text-muted small">
              {lang === 'es'
                ? 'Gestiona las universidades aliadas e instituciones registradas en el sistema.'
                : 'Manage partner universities and institutions registered in the platform.'}
            </span>
          </div>
        </div>

        <div className="row g-4">
          {/* Form Card */}
          <div className="col-12 col-lg-4">
            <div
              className="card border-0 shadow-sm rounded-4 bg-white sticky-top"
              style={{ top: '20px' }}
            >
              <div className="card-body p-4">
                <h5 className="fw-bold text-sen mb-3 d-flex align-items-center gap-2">
                  <i className={isUpdate ? 'fa fa-edit' : 'fa fa-plus-circle'}></i>
                  <span>
                    {isUpdate ? texts.admin_common.update : texts.admin_common.save}{' '}
                    {texts.admin.menu_universities}
                  </span>
                </h5>

                <div className="mb-3">
                  <label htmlFor="uni-name" className="form-label small fw-bold text-secondary">
                    {texts.admin_common.name}
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-university"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control bg-light border-start-0"
                      id="uni-name"
                      placeholder="Nombre de la Universidad"
                      onChange={e => setNewNameUniversity(e.target.value)}
                      value={newNameUniversity}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="uni-slug" className="form-label small fw-bold text-secondary">
                    URL / Slug
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-link"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control bg-light border-start-0"
                      id="uni-slug"
                      placeholder="url-amigable-universidad"
                      onChange={e => setNewSlugUniversity(e.target.value)}
                      value={newSlugUniversity}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                  {isUpdate && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-3"
                      onClick={() => {
                        setNewNameUniversity('');
                        setNewSlugUniversity('');
                        setIdToUpdate('');
                        setIsUpdate(false);
                      }}
                    >
                      {texts.admin_common.cancel}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary bg-sen border-0 px-4 fw-bold shadow-sm"
                    onClick={isUpdate ? handleUpdateUniversity : handleSaveUniversity}
                  >
                    {isUpdate ? texts.admin_common.update : texts.admin_common.save}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="col-12 col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="py-3 px-4 text-secondary small fw-bold text-uppercase">
                          {texts.admin_common.name}
                        </th>
                        <th className="py-3 px-3 text-secondary small fw-bold text-uppercase">
                          URL Pública
                        </th>
                        <th className="py-3 px-4 text-secondary small fw-bold text-uppercase text-end">
                          {texts.admin_common.options}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan="3" className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">{texts.admin.loading}</span>
                            </div>
                          </td>
                        </tr>
                      ) : universities.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="text-center py-5 text-muted">
                            <i className="fa fa-folder-open-o fs-3 d-block mb-2 text-secondary"></i>
                            No hay universidades registradas
                          </td>
                        </tr>
                      ) : (
                        universities.map(university => (
                          <tr key={university.id}>
                            <td className="py-3 px-4 fw-semibold text-dark">{university.name}</td>
                            <td className="py-3 px-3">
                              <a
                                href={`${process.env.REACT_APP_FRONTEND_URL}/page/${university.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-decoration-none small badge bg-light text-primary border"
                              >
                                <i className="fa fa-external-link me-1"></i>
                                /page/{university.slug}
                              </a>
                            </td>
                            <td className="py-3 px-4 text-end">
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => handleEditUniversity(university.id)}
                                title="Editar"
                              >
                                <i className="fa fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteUniversity(university.id)}
                                title="Eliminar"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Universities;
